import { DeviceTypeDto } from "./device-dto";
import { transform } from "./transformer";

export interface IDeviceGroupDataInput {
    [attributeName: string]: string | number | boolean;
}

export interface ITimeInterval {
    startTime : string;
    endTime: string;
}
export interface IDeviceGroupOutOfServiceSettingsProps {
    outOfServiceWindows: ITimeInterval[];
}

export interface IDeviceGroupOptions {
    id: number;
    accountUuid: string;
    deviceGroupName?: string;
    deviceGroupLabel?: string;
    deviceGroupDescription?: string;
    deviceGroupPlatform?: string;
    deviceParentGroupName?: string;
    deviceGroupAttribute?: IDeviceGroupDataInput;
    deviceGroupOutOfServiceSettings?: IDeviceGroupOutOfServiceSettingsProps;
    deviceGroupTimezone?: string;
    deviceGroupType?: 'isClient' | 'isSystem';
    parentGroup?: IDeviceGroupOptions;
    childrenGroups?: IDeviceGroupOptions[];
    devices?: any;
    deploymentGroup: any;
    deploymentGroupId: number;
}

export type TCreateDeviceGroupOptions = Omit<IDeviceGroupOptions, 'deviceGroupName' | 'parentGroup' | 'childrenGroups'>;

export type TUpdateDeviceGroupOptions = Omit<IDeviceGroupOptions, 'parentGroup' | 'childrenGroups' | 'deviceGroupPlatform'>

export interface IMoveDeviceToGroupResponseOptions {
    identifier: string;
    success: boolean;
    error?: string;
}

export interface IUpdateDeviceResultOptions {
    succeeded: string[];
    errors: Omit<IMoveDeviceToGroupResponseOptions, 'success'>[];
}


export class PlatformDto {
    constructor(private readonly _object: Object) { }

    get code(): string {
        return this._object['code'] || 'UNKNOWN';
    }

    get name(): string {
        return this._object['name'] || 'UNKNOWN';
    }

    get deviceTypes(): DeviceTypeDto[] {
        return this._object['deviceTypes'] || [];
    }
}

export class DeviceGroupDto implements IDeviceGroupOptions {
    private _parentGroup: DeviceGroupDto | undefined;
    private _childrenGroups: DeviceGroupDto[];

    constructor(private readonly _object: Object = {}) {
        this._parentGroup = this._object?.['parentGroup'] ? transform(DeviceGroupDto, this._object['parentGroup'] as Object) : undefined;
        this._childrenGroups = this._object?.['childrenGroups'] ? transform(DeviceGroupDto, this._object['childrenGroups'] as unknown[]) : [];
    }

    get id(): number {
        return this._object?.['id'] || 0;
    }

    get accountUuid(): string {
        return this._object?.['accountUuid'] || 'UNKNOWN';
    }

    get deviceGroupName(): string {
        return this._object?.['deviceGroupName'] || 'UNKNOWN_NAME';
    }

    get deviceGroupLabel(): string {
        return this._object?.['deviceGroupLabel'] || 'UNKNOWN LABEL';
    }

    get deviceGroupDescription(): string {
        return this._object?.['deviceGroupDescription'] || '';
    }

    get deviceGroupPlatform(): string {
        return this._object?.['deviceGroupPlatform'] || 'UNKNOWN PLATFORM';
    }

    get deviceParentGroupName(): string {
        return this._object?.['deviceParentGroupName'] || '';
    }

    get deviceGroupAttribute(): IDeviceGroupDataInput {
        return this._object?.['deviceGroupAttribute'] || {};
    }

    get deviceGroupOutOfServiceSettings(): IDeviceGroupOutOfServiceSettingsProps {
        return this._object?.['deviceGroupOutOfServiceSettings'] || {};
    }

    get deviceGroupTimezone(): string {
        return this._object?.['deviceGroupTimezone'] || '';
    }

    get deviceGroupType(): 'isClient' | 'isSystem' {
        return this._object?.['deviceGroupType'] || 'isClient';
    }

    get parentGroup(): DeviceGroupDto | undefined {
        return this._parentGroup;
    }

    get childrenGroups(): DeviceGroupDto[] {
        return this._childrenGroups;
    }

    get devices(): any {
        return this._object?.['devices'] || null;
    }

    get deploymentGroup(): any {
        return this._object?.['deploymentGroup'] || null;
    }

    get deploymentGroupId(): number {
        return this._object?.['deploymentGroupId'] || null;
    }

    get displayName(): string {
        return this.deviceGroupType === 'isClient' ? this.deviceGroupLabel : `${this.deviceGroupLabel} - ${this.deviceGroupPlatform}`;
    }

    clone(overrides?: Object): DeviceGroupDto {
        return new DeviceGroupDto({
            ...(this.toJson()),
            ...(overrides || {})
        });
    }

    toJson(): Object {
        return this._object;
    }
}